import PopupBase from "@luminecence/composants/PopupBase"
import { initializeApp } from "firebase/app"
import {
  AuthErrorCodes,
  connectAuthEmulator,
  createUserWithEmailAndPassword,
  deleteUser,
  GoogleAuthProvider,
  getAdditionalUserInfo,
  getAuth,
  getRedirectResult,
  linkWithCredential,
  linkWithPopup,
  OAuthProvider,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateProfile,
  validatePassword,
} from "firebase/auth"
import "@luminecence/composants/PopupConfirmation"
import "@luminecence/composants/PopupInformation"
import FirebaseBaseDeDonnees from "./FirebaseBaseDeDonnees.js"
import { FIREBASE_MESSAGES } from "./FirebaseMessagesConstantes.js"

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
}

/**
 * @typedef {import('firebase/auth').User} User
 */

export default class Firebase {
  app = null

  /** @type {FirebaseBaseDeDonnees} */
  bdd = null

  /** @type {import('./GestionnaireChangementConnexion.js')} */
  gestionnaireChangementConnexion = null

  /** @type {import('firebase/auth').AuthCredential|null} */
  credentialEnAttente = null

  /** @type {Promise<void>|null} */
  authPret = null

  /** @type {(() => void)|null} */
  resolveAuthPret = null

  authPretResolue = false

  constructor() {
    this.app = initializeApp(firebaseConfig)
    this.bdd = new FirebaseBaseDeDonnees(this.app)
    this.authPret = new Promise((resolve) => {
      this.resolveAuthPret = resolve
    })

    if (this.estModeLocalhost()) {
      connectAuthEmulator(this.getServiceAuth(), "http://localhost:9099")
    } else if (this.estModeRemotePhone()) {
      connectAuthEmulator(this.getServiceAuth(), `http://${import.meta.env.VITE_IP_PHONE_EMULATEUR}:9099`)
    }
    this.initialiserConnexionRedirect()
    onAuthStateChanged(this.getServiceAuth(), async (user) => {
      if (!this.authPretResolue) {
        this.authPretResolue = true
        this.resolveAuthPret?.()
      }
      await Promise.resolve()
      if (user) {
        this.gestionnaireChangementConnexion?.declencherCallbackUtilisateurConnecte?.(user.displayName)
        this.ajouterUnTokenDeNotificationPourUneConversation()
      } else {
        this.gestionnaireChangementConnexion?.declencherCallbackUtilisateurDeconnecte?.()
      }
    })
  }

  /**
   * Vérifie si on est en mode localhost pour le developpement et mocks de firebase
   * @returns {boolean}
   */
  estModeLocalhost() {
    return location.hostname === "localhost"
  }

  estModeRemotePhone() {
    return location.hostname === import.meta.env.VITE_IP_PHONE_EMULATEUR
  }

  getServiceAuth() {
    return getAuth(this.app)
  }

  getBaseDeDonnees() {
    return this.bdd
  }

  /**
   * Retourne l'utilisateur s'il est connecté sinon null
   * @returns {User | null}
   */
  getUser() {
    return this.getServiceAuth().currentUser
  }

  attendreAuthPret() {
    return this.authPret ?? Promise.resolve()
  }

  async initialiserConnexionRedirect() {
    const auth = this.getServiceAuth()

    try {
      const resultat = await getRedirectResult(auth)

      if (!resultat) {
        return
      }

      const user = resultat.user,
        infoUser = getAdditionalUserInfo(resultat)

      console.log("Utilisateur OAuth connecté:", user)
      console.log("Infos utilisateur:", infoUser)

      // Attendre que la base de données soit prête avant d'ajouter le membre
      await this.attendreAuthPret()
      await window.authentification.bdd.membres.ajouterUnMembre(user.displayName, window.authentification.getUser().uid, user.email)

      // Attendre que le gestionnaire de changement connexion soit initialisé (créé par le composant Connexion)
      let tentatives = 0
      while (!this.gestionnaireChangementConnexion && tentatives < 100) {
        await new Promise((resolve) => setTimeout(resolve, 50))
        tentatives++
      }

      // Déclencher le callback de connexion
      if (this.gestionnaireChangementConnexion) {
        this.gestionnaireChangementConnexion.declencherCallbackUtilisateurConnecte(user.displayName)
      }
    } catch (error) {
      console.error("Erreur lors de l'initialisation de la connexion par redirection:", error)
    }
  }

  async verifierMotDePasse(mdp) {
    if (this.estModeLocalhost() || this.estModeRemotePhone()) {
      // En mode localhost, on ne fait pas de validation stricte
      return ""
    }
    let validateurMotDePasse = null

    try {
      validateurMotDePasse = await validatePassword(this.getServiceAuth(), mdp)

      if (validateurMotDePasse.isValid) {
        return ""
      }

      throw new Error(validateurMotDePasse.errors.join(", "))
    } catch (error) {
      const messageErreur = "Exigences du mot de passe non respectées, il faut au moins 6 caractères"

      console.error(error)
      throw new Error(messageErreur)
    }
  }

  /**
   * Inscri l'utilisateur avec email et mot de passe
   * @param {string} email
   * @param {string} password
   * @param {string} pseudo
   * @return {Promise<import('firebase/auth').UserCredential|null>}
   */
  async inscrireUtilisateur(email, password, pseudo) {
    const auth = this.getServiceAuth()

    try {
      const mdpValide = await this.verifierMotDePasse(password)

      if (mdpValide === "") {
        try {
          const userCredential = await createUserWithEmailAndPassword(auth, email, password)
          // L'utilisateur est inscrit
          const user = userCredential.user

          //on met à jour le profil avec le pseudo
          await updateProfile(user, { displayName: pseudo })
          this.gestionnaireChangementConnexion.declencherCallbackUtilisateurConnecte(pseudo)
          return userCredential
        } catch (error) {
          this.gererErreurInscriptionEtConnexion(error)
        }
      } else {
        console.error(FIREBASE_MESSAGES.MOT_DE_PASSE_NON_VALIDE)
        throw new Error(mdpValide)
      }
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  gererErreurInscriptionEtConnexion(error) {
    const errorCode = error.code

    if (errorCode === AuthErrorCodes.USER_DELETED) {
      throw new Error("L'utilisateur avec cet email n'existe pas", { cause: error })
    }

    if (errorCode === AuthErrorCodes.INVALID_PASSWORD) {
      throw new Error("Le mot de passe est incorrect", { cause: error })
    }

    if (error.code === AuthErrorCodes.INVALID_EMAIL) {
      throw new Error("L'adresse email n'est pas valide", { cause: error })
    }

    if (error.code === AuthErrorCodes.INVALID_LOGIN_CREDENTIALS || error.code === "auth/invalid-credential") {
      throw new Error("Identifiants invalides", { cause: error })
    }

    throw new Error(error)
  }

  /**
   * Connecte l'utilisateur avec email et mot de passe
   * @param {string} email
   * @param {string} password
   */
  async connecterUtilisateur(email, password) {
    const auth = this.getServiceAuth()
    try {
      await signInWithEmailAndPassword(auth, email, password)

      if (this.credentialEnAttente && auth.currentUser) {
        await linkWithCredential(auth.currentUser, this.credentialEnAttente)
        this.credentialEnAttente = null
      }
    } catch (error) {
      this.gererErreurInscriptionEtConnexion(error)
    }
  }

  async supprimerUtilisateurConnecte() {
    const user = this.getUser()
    if (!user) {
      return
    }

    await this.bdd.membres.supprimerMembreEtPokemons(user.uid)
    await deleteUser(user)
  }

  async supprimerPokemonsUtilisateurConnecte() {
    const user = this.getUser()
    if (!user) {
      return
    }

    await this.bdd.pokemons.supprimerPokemons(user.uid)
  }

  async deconnecterUtilisateur() {
    const auth = getAuth()

    try {
      await signOut(auth)
      console.log(FIREBASE_MESSAGES.DECONNEXION_OK)
    } catch (_error) {
      console.log(FIREBASE_MESSAGES.DECONNEXION_KO)
    }
  }

  async reinitialiserMotDePasse(email) {
    const auth = this.getServiceAuth()

    try {
      await sendPasswordResetEmail(auth, email)
      await PopupBase.ouvrirEtAttendre("p-popup-information", {
        titre: "Email de réinitialisation envoyé",
        texte: `Un email de réinitialisation du mot de passe a été envoyé à ${email} si un compte existe avec cette adresse.`,
      })
    } catch (error) {
      console.error("Erreur lors de l'envoi de l'email de réinitialisation du mot de passe", error)
      throw new Error("Erreur lors de l'envoi de l'email de réinitialisation du mot de passe")
    }
  }

  /**
   * @returns {Promise<import('firebase/auth').User>}
   */
  async connecterUtilisateurAvecGoogle() {
    const fournisseur = new GoogleAuthProvider(),
      auth = this.getServiceAuth()

    try {
      // Utiliser signInWithPopup au lieu de signInWithRedirect
      // signInWithPopup ouvre un popup, ne redirige pas la page
      // C'est plus compatible avec Netlify que signInWithRedirect
      const resultat = auth.currentUser ? await linkWithPopup(auth.currentUser, fournisseur) : await signInWithPopup(auth, fournisseur)

      // Le résultat est disponible immédiatement
      const user = resultat.user

      // Ajouter le membre à la base de données
      await this.bdd.membres.ajouterUnMembre(user.displayName, user.uid, user.email)
    } catch (erreur) {
      if (erreur.code === AuthErrorCodes.ACCOUNT_EXISTS_WITH_DIFFERENT_CREDENTIAL || erreur.code === "auth/account-exists-with-different-credential") {
        const credential = GoogleAuthProvider.credentialFromError(erreur)
        this.credentialEnAttente = credential
        throw new Error("Un compte existe déjà avec cet email. Connectez-vous avec email/mot de passe pour lier automatiquement les comptes.", {
          cause: erreur,
        })
      }

      console.error("Erreur connexion Google:", erreur)
      throw erreur
    }
  }

  async connecterUtilisateurAvecMicrosoft() {
    const fournisseur = new OAuthProvider("microsoft.com"),
      auth = this.getServiceAuth()

    try {
      // Utiliser signInWithPopup au lieu de signInWithRedirect
      // signInWithPopup ouvre un popup, ne redirige pas la page
      // C'est plus compatible avec Netlify que signInWithRedirect
      const resultat = auth.currentUser ? await linkWithPopup(auth.currentUser, fournisseur) : await signInWithPopup(auth, fournisseur)

      // Le résultat est disponible immédiatement
      const user = resultat.user

      // Ajouter le membre à la base de données
      await this.bdd.membres.ajouterUnMembre(user.displayName, user.uid, user.email)
    } catch (erreur) {
      if (erreur.code === AuthErrorCodes.ACCOUNT_EXISTS_WITH_DIFFERENT_CREDENTIAL || erreur.code === "auth/account-exists-with-different-credential") {
        const credential = OAuthProvider.credentialFromError(erreur)
        this.credentialEnAttente = credential
        throw new Error("Un compte existe déjà avec cet email. Connectez-vous avec email/mot de passe pour lier automatiquement les comptes.", {
          cause: erreur,
        })
      }

      console.error("Erreur connexion Microsoft:", erreur)
      throw erreur
    }
  }

  async connecterUtilisateurAvecDiscord() {
    // "oidc.discord" est l'ID que tu as donné à ton fournisseur dans Firebase
    const provider = new OAuthProvider("oidc.discord.com")

    provider.addScope("identify")
    provider.addScope("email")
    const auth = this.getServiceAuth()

    try {
      const result = await signInWithPopup(auth, provider)
      const details = getAdditionalUserInfo(result)
      const pseudo = details?.profile?.nickname || "Utilisateur Discord"
      const email = details?.profile?.email || ""

      await this.bdd.membres.ajouterUnMembre(pseudo, result.user.uid, email)

      return pseudo
    } catch (erreur) {
      console.error("Erreur lors de la connexion :", erreur.code)
      throw erreur
    }
  }

  async ajouterUnTokenDeNotificationPourUneConversation() {
    if (typeof window === "undefined" || !window.authentification?.getUser?.()) {
      return
    }

    if (!("Notification" in window)) {
      console.warn("Notifications non supportees par ce navigateur")
      return
    }

    if (!("serviceWorker" in navigator)) {
      console.warn("Service Worker indisponible: notifications push desactivees")
      return
    }

    if (Notification.permission === "denied") {
      console.warn("Notifications bloquees par le navigateur")
      return
    }

    if (Notification.permission !== "granted") {
      const optionsPopup = {
        titre: "Activer les notifications",
        modal: true,
        texte: "Si vous souhaitez recevoir des notifications pour les nouveaux messages dans vos conversations, veuillez autoriser les notifications.",
      }
      const resultat = await PopupBase.ouvrirEtAttendre("p-popup-information", optionsPopup)

      if (!resultat) {
        return
      }

      const permission = await Notification.requestPermission()
      if (permission !== "granted") {
        console.warn("Permission de notification refusee ou ignoree")
        return
      }
    }

    console.log("notif acceptee")
    const idUtilisateurConnecte = window.authentification.getUser().uid
    await window.authentification.bdd.membres.ajouterUnTokenDeNotificationPourUneConversation(idUtilisateurConnecte)
  }
}
